import { AiSearch, DiscoverFilters } from "@/features";
import { BookmarkTree } from "@/features/bookmark";
import { Europafacts } from "@/services/api";
import type { DatasetResponse } from "@/types/europafacts";
import { Flex, Text } from "@mantine/core";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import "./DiscoverResultsLayout.css";

const PAGINATION_LIMIT = 10;

export function DiscoverResultsLayout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const defaultPrompt = searchParams.get("q");

  const queryFn = useCallback(
    async ({ pageParam }: { pageParam: number }) => {
      const params = new URLSearchParams(searchParams);
      const limitParam = params.get("limit");
      const limit = limitParam ? parseInt(limitParam, 10) : PAGINATION_LIMIT;
      const offset = pageParam * limit;
      const page = pageParam + 1;

      params.set("_limit", limit.toString());
      params.set("_offset", offset.toString());
      params.set("_page_no", page.toString());

      const paramsString = params.toString().replaceAll("+", "%26");
      const url = `/db/db-table/list/api?${paramsString}`;
      const res = await Europafacts.fetch<DatasetResponse>(url, {
        method: "GET",
      });
      return res.data;
    },
    [searchParams]
  );

  const {
    data,
    isLoading: loading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    DatasetResponse,
    Error,
    InfiniteData<DatasetResponse>,
    readonly unknown[],
    number
  >({
    queryKey: ["discover-results", searchParams.toString()],
    queryFn,
    enabled: (defaultPrompt?.length ?? 0) > 0,
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: getNextPageParam,
  });

  function getNextPageParam(
    lastPage: DatasetResponse,
    allPages: DatasetResponse[]
  ) {
    const totalLoaded = allPages.reduce(
      (sum, page) => sum + page.results.length,
      0
    );
    if (totalLoaded < lastPage.total_count) {
      return allPages.length;
    }
  }

  const datasets = useMemo(() => {
    const allDatasets = data?.pages.flatMap((page) => page.results) || [];
    const seen = new Set<string>();
    return allDatasets.filter((dataset) => {
      if (seen.has(dataset.db_table_id)) {
        return false;
      }
      seen.add(dataset.db_table_id);
      return true;
    });
  }, [data]);

  const result = data?.pages[0];

  const blueCoverStyle = useMemo(
    () => ({ zIndex: 9, viewTransitionName: "blue-cover" }),
    []
  );

  const stickySearchStyle = useMemo(
    () => ({
      zIndex: 10,
      borderBottom: "1px solid var(--mantine-color-border-1)",
    }),
    []
  );

  const outletContext = useMemo(
    () => ({
      datasets,
      loading,
      error,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    }),
    [datasets, loading, error, fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  async function onPrompt(prompt: string) {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("q", prompt);
    navigate(`/discover/search?${newParams.toString()}`, {
      viewTransition: false,
    });
  }

  const resultCountLabel = useMemo(() => {
    return `${(result?.total_count ?? 0).toLocaleString("da-DK", {
      maximumFractionDigits: 0,
    })} results`;
  }, [result]);

  return (
    <main className="discover-results-main">
      <Flex direction="column" w="100%" align="center" h="100%" px={70}>
        <Flex
          top={45}
          h={0}
          pos="fixed"
          bg="eu_flag.9"
          w="100%"
          style={blueCoverStyle}
        />
        <Flex justify="center" w="100%" maw={1200} gap={40} pos="relative">
          <Flex
            pos="fixed"
            top={45}
            bottom={0}
            left={0}
            w={300}
            maw="calc(100vw - 650px - 300px)"
            px="md"
            pb={120}
            bg="background.0"
            direction="column"
            flex={1}
            className="discover-bookmarks"
          >
            <Flex bg="background.0" pos="sticky" top={0} pb={8} pt={20}>
              <Text fz={15} fw={600} mb={4}>
                Saved Datasets
              </Text>
            </Flex>
            <BookmarkTree onDatasetSelect={() => {}} />
          </Flex>
          <Flex
            direction="column"
            style={{ flexBasis: 650 }}
            pos="relative"
            h="100%"
          >
            <Flex
              pos="sticky"
              top={45}
              right={0}
              left={0}
              style={stickySearchStyle}
              pt={20}
              pb={10}
              direction="column"
              gap={8}
              bg="white"
            >
              <AiSearch
                loading={loading}
                defaultPrompt={defaultPrompt}
                onPrompt={onPrompt}
                viewTransitionName="ai-search"
              />
              <DiscoverFilters label={resultCountLabel} />
            </Flex>
            <Outlet context={outletContext} />
          </Flex>
        </Flex>
      </Flex>
    </main>
  );
}
